/**
 * CR-6439: customer-view (PO Headers) sections of the headers filter panel,
 * laid out per the supplied design diagram:
 *
 *   Company        — Site, Division, Country, Warehouse
 *   Supplier       — Group, Site, Country, Code + Strategic suppliers only
 *   Classification — Commodity Group, Commodity Type, Supplier Type, Order Type
 *   Order Details  — PO Number, Status, PDF Version Attached, Ack Status
 *
 * CR-6439 rework: client testing found Supplier Code, Supplier Type and Order
 * Type missing, and the commodity filters sitting in their own "Commodity"
 * section that only appeared when the tenant had commodity groups. They now
 * live in a Classification section that is always shown, matching the PO
 * Lines panel (CR-6347). Order Type reads the ERP order-type codes present in
 * scope; the dormant ``order_type`` column is deliberately not offered
 * (CR-7273).
 *
 * "Linked Status" from the diagram is not implemented: the platform holds no
 * linked-status data on a PO header (the column was a placeholder and was
 * removed in 1.42.0), so a control could only ever return everything.
 *
 * Input types are preserved exactly as the ticket requires. Cascades
 * (Division / Country → Site → Warehouse & Supplier, Supplier Country /
 * Group → Supplier, Commodity Group → Type) come from usePOHeaderFilterCascades.
 */
import { Building2, Users, Tags, FileText } from 'lucide-react';
import SearchableSelect from '../../../ui/SearchableSelect';
import { FilterSection, FilterGrid, FilterField, filterControlClass } from '../../../filters/FilterSection';
import { SUPPLIER_TYPE_OPTIONS } from '../po-lines/types';
import { SectionDivider, STATUS_OPTIONS, ACK_STATUS_OPTIONS, POHeaderPanelProps } from './poHeaderFilterShared';
import { useFilterVisibility } from '../../../../hooks/useFilterVisibility';

export function POHeaderCustomerFilterPanel({
  filters,
  textInputs,
  siteOptions,
  warehouseOptions,
  divisionOptions,
  siteCountries,
  supplierCountries,
  supplierGroupOptions,
  supplierSiteOptions,
  commodityGroupOptions,
  commodityTypeOptions,
  orderTypeOptions,
  handlers,
  onFilterChange,
  onApply,
}: POHeaderPanelProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  const {
    handleDivisionChange,
    handleCompanyCountryChange,
    handleSiteChange,
    handleSupplierCountryChange,
    handleSupplierGroupChange,
    handleCommodityGroupChange,
  } = handlers;

  return (
    <>
      {/* COMPANY section - CR-6439: Site, Division, Country, Warehouse per the design */}
      {/* CR-6446: Warehouse with Site → Warehouse cascade */}
      <FilterSection title="Company" icon={Building2}>
        <FilterGrid>
          <FilterField label="Site">
            <SearchableSelect
              options={siteOptions}
              value={filters.site}
              onChange={(val) => handleSiteChange(val)}
              placeholder="All sites"
            />
          </FilterField>
          {fv.division && (
          <FilterField label="Division">
            <select
              value={filters.division}
              onChange={(e) => handleDivisionChange(e.target.value)}
              className={filterControlClass}
            >
              <option value="">All Divisions</option>
              {divisionOptions.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </FilterField>
          )}
          {fv.companyCountry && (
          <FilterField label="Country">
            <select
              value={filters.companyCountry}
              onChange={(e) => handleCompanyCountryChange(e.target.value)}
              className={filterControlClass}
            >
              <option value="">All Countries</option>
              {siteCountries.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </FilterField>
          )}
          {fv.warehouse && (
          <FilterField label="Warehouse">
            <select
              value={filters.warehouse}
              onChange={(e) => onFilterChange('warehouse', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All Warehouses</option>
              {warehouseOptions.map((w) => (
                <option key={w.value} value={w.value}>{w.label}</option>
              ))}
            </select>
          </FilterField>
          )}
        </FilterGrid>
      </FilterSection>

      <SectionDivider />

      {/* SUPPLIER section - CR-6439: Group, Site, Country, Code per the design */}
      <FilterSection title="Supplier" icon={Users}>
        <FilterGrid>
          <FilterField label="Group">
            <SearchableSelect
              options={supplierGroupOptions}
              value={filters.supplierGroup}
              onChange={(val) => handleSupplierGroupChange(val)}
              placeholder="All supplier groups"
            />
          </FilterField>
          {/* CR-7232: bound to textInputs.supplier, the slot onFilterChange
              actually writes and the toolbar box shares. It read
              filters.supplier before, which nothing but the ?supplier= deep
              link set — so picking a supplier here filtered the grid but left
              this select showing "All suppliers". */}
          <FilterField label="Site">
            <SearchableSelect
              options={supplierSiteOptions}
              value={textInputs.supplier}
              onChange={(val) => onFilterChange('supplier', val)}
              placeholder="All supplier sites"
            />
          </FilterField>
          <FilterField label="Country">
            <select
              value={filters.supplierCountry}
              onChange={(e) => handleSupplierCountryChange(e.target.value)}
              className={filterControlClass}
            >
              <option value="">All Countries</option>
              {supplierCountries.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </FilterField>
          {/* CR-6439 rework: Supplier Code — contains match on the supplier
              company's code (CR-7028 backend filter). */}
          {fv.supplierCode && (
          <FilterField label="Code">
            <input
              type="text"
              placeholder="Enter supplier code..."
              value={filters.supplierCode}
              onChange={(e) => onFilterChange('supplierCode', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onApply?.(); }}
              className={filterControlClass}
            />
          </FilterField>
          )}
        </FilterGrid>
        <div className="flex items-center gap-3 mt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.strategic === 'yes'}
              onChange={(e) => onFilterChange('strategic', e.target.checked ? 'yes' : '')}
              className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm font-medium">Strategic suppliers only</span>
          </label>
        </div>
      </FilterSection>

      <SectionDivider />

      {/* CLASSIFICATION section - CR-6439 rework: Commodity Group → Type,
          Supplier Type, Order Type per the design. Always shown. */}
      <FilterSection title="Classification" icon={Tags}>
        <FilterGrid>
          {fv.commodity && (<>
          <FilterField label="Commodity Group">
            <select
              value={filters.commodityGroup}
              onChange={(e) => handleCommodityGroupChange(e.target.value)}
              className={filterControlClass}
            >
              <option value="">All commodity groups</option>
              {commodityGroupOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Commodity Type">
            <select
              value={filters.commodityType}
              onChange={(e) => onFilterChange('commodityType', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All commodity types</option>
              {commodityTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </FilterField>
          </>)}
          {/* Supplier Type: internal / external relative to the customer
              organisation in scope — same values as the PO Lines panel. */}
          {fv.supplierType && (
          <FilterField label="Supplier Type">
            <select
              value={filters.supplierType}
              onChange={(e) => onFilterChange('supplierType', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All suppliers</option>
              {SUPPLIER_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </FilterField>
          )}
          {/* Order Type: the ERP order-type codes present in scope. A tenant
              whose feed carries no codes gets an honest "none in scope" hint
              rather than a dropdown that returns nothing. */}
          <FilterField
            label={
              <>
                Order Type
                {orderTypeOptions.length === 0 && (
                  <span className="ml-1 font-normal text-muted-foreground">(none in scope)</span>
                )}
              </>
            }
          >
            <select
              value={filters.orderType}
              onChange={(e) => onFilterChange('orderType', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All order types</option>
              {orderTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </FilterField>
        </FilterGrid>
      </FilterSection>

      <SectionDivider />

      {/* ORDER DETAILS section */}
      <FilterSection title="Order Details" icon={FileText}>
        <FilterGrid>
          <FilterField label="PO Number">
            <input
              type="text"
              placeholder="Enter PO Number..."
              value={textInputs.poNumber}
              onChange={(e) => onFilterChange('poNumber', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onApply?.(); }}
              className={filterControlClass}
            />
          </FilterField>
          <FilterField label="Status">
            <select
              value={filters.status}
              onChange={(e) => onFilterChange('status', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="PDF Version Attached">
            <select
              value={filters.pdfAttached}
              onChange={(e) => onFilterChange('pdfAttached', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </FilterField>
          {/* CR-6679: Header-level acknowledgement status filter */}
          <FilterField label="Ack Status">
            <select
              value={filters.ackStatus}
              onChange={(e) => onFilterChange('ackStatus', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All</option>
              {ACK_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </FilterField>
        </FilterGrid>
      </FilterSection>
    </>
  );
}
